import type { Union } from "../type-utils";

export enum ActivityType {
  CandidateAdded = "CandidateAdded",
  CandidateDeleted = "CandidateDeleted",
  CandidateUpdated = "CandidateUpdated",
  CandidateBirthday = "CandidateBirthday",
  CandidateBirthdaySoon = "CandidateBirthdaySoon",
  FileUploaded = "FileUploaded",
  FileModified = "FileModified",
  DocumentSetMetaDataModified = "DocumentSetMetaDataModified",
  FileDeleted = "FileDeleted",
  TranslationUploaded = "TranslationUploaded",
  TranslationFailed = "TranslationFailed",
  FreeFormatFileUploaded = "FreeFormatFileUploaded",
  FreeFormatFileDeleted = "FreeFormatFileDeleted",
  FileExpiresSoon = "FileExpiresSoon",
  CollectionAdded = "CollectionAdded",
  CollectionDeleted = "CollectionDeleted",
  CollectionUpdated = "CollectionUpdated",
  CandidateAddedToCollection = "CandidateAddedToCollection",
  CandidateRemovedFromCollection = "CandidateRemovedFromCollection",
  CollectionEmbeddedIntoCollection = "CollectionEmbeddedIntoCollection",
  CollectionRemovedFromCollection = "CollectionRemovedFromCollection",
  CandidateDeletionReminder = "CandidateDeletionReminder",
  SharingAdded = "SharingAdded",
  SharingDeleted = "SharingDeleted",
  SharingUpdated = "SharingUpdated",
  SharingEnabled = "SharingEnabled",
  SharingDisabled = "SharingDisabled",
  SharingTypeModified = "SharingTypeModified",
  TalentPoolRequestReceived = "TalentPoolRequestReceived",
  TalentPoolRequestApproved = "TalentPoolRequestApproved",
  TalentPoolRequestRejected = "TalentPoolRequestRejected",
  ProfileMaskUpdated = "ProfileMaskUpdated",
  TaskStarted = "TaskStarted",
  TaskManuallyChanged = "TaskManuallyChanged",
  TaskChanged = "TaskChanged",
  TaskFinished = "TaskFinished",
  TaskCancelled = "TaskCancelled",
  TaskFailed = "TaskFailed",
  TaskStopped = "TaskStopped",
  TaskReminderSent = "TaskReminderSent",
  TaskDelegatedByRule = "TaskDelegatedByRule",
  TaskDelegationRevokedByRule = "TaskDelegationRevokedByRule",
  TaskDeleted = "TaskDeleted",
  TaskReset = "TaskReset",
  TaskManuallyCreated = "TaskManuallyCreated",
  TaskStateChanged = "TaskStateChanged",
  TaskStateManuallyChanged = "TaskStateManuallyChanged",
  EmailNotDelivered = "EmailNotDelivered",
  CandidateLoggedIn = "CandidateLoggedIn",
  FeedbackProvided = "FeedbackProvided",
  FeedbackCommentAdded = "FeedbackCommentAdded",
  CandidateDroppedOut = "CandidateDroppedOut",
  AutoProcessOperationTriggered = "AutoProcessOperationTriggered",
  AutoProcessOperationFailed = "AutoProcessOperationFailed",
  TaskNoteAdded = "TaskNoteAdded",
  CandidateRequestDeletion = "CandidateRequestDeletion",
  CandidateOptedOut = "CandidateOptedOut",
  CandidateOptedIn = "CandidateOptedIn",
  UserLoggedIn = "UserLoggedIn",
  ProcessDeleted = "ProcessDeleted",
  ProcessCreated = "ProcessCreated",
  ProcessReset = "ProcessReset",
  ProcessModelPublished = "ProcessModelPublished",
  ProcessModelUnpublished = "ProcessModelUnpublished",
  ProcessModelReleaseCreated = "ProcessModelReleaseCreated",
  ProcessModelDeleted = "ProcessModelDeleted",
  EducationExamDeleted = "EducationExamDeleted",
  EducationExamUpdated = "EducationExamUpdated",
  EducationExamCreated = "EducationExamCreated",
  EducationExamResultCreated = "EducationExamResultCreated",
  EducationExamResultDeleted = "EducationExamResultDeleted",
  CandidateAddedToExam = "CandidateAddedToExam",
  CandidateRemovedFromExam = "CandidateRemovedFromExam",
  CandidateNotFound = "CandidateNotFound",
  UserNotFound = "UserNotFound",
  ProcessNotFound = "ProcessNotFound",
  DocumentNotFound = "DocumentNotFound",
  OrganizationNotFound = "OrganizationNotFound",
  CollectionNotFound = "CollectionNotFound",
  TaskNotFound = "TaskNotFound",
  InvalidUserGroupRoleMapping = "InvalidUserGroupRoleMapping",
  AutoTeamRoleNotSetOrOwner = "AutoTeamRoleNotSetOrOwner",
  AutoTeamNoResponsibleUser = "AutoTeamNoResponsibleUser",
  InvalidSharingTemplate = "InvalidSharingTemplate",
  OrganizationNotLinked = "OrganizationNotLinked",
  ProcessModelNotFound = "ProcessModelNotFound",
  BaseProcessNotFound = "BaseProcessNotFound",
  ContractCreated = "ContractCreated",
  ContractCancelled = "ContractCancelled",
  ContractCancellationWithdrawn = "ContractCancellationWithdrawn",
  ContractAnnulled = "ContractAnnulled",
  ContractPlanChanged = "ContractPlanChanged",
  InvoicePaid = "InvoicePaid",
  OnlineApplicationRun = "OnlineApplicationRun",
  ConsentFeedback = "ConsentFeedback",
  CvParsingRequested = "CvParsingRequested",
  ChildOrganizationLinksSynced = "ChildOrganizationLinksSynced",
  InterviewIterationAdded = "InterviewIterationAdded",
  InterviewIterationDeleted = "InterviewIterationDeleted",
  InterviewEmployerUpdated = "InterviewEmployerUpdated",
  InterviewFeedbackAdded = "InterviewFeedbackAdded",
  InterviewFeedbackUpdated = "InterviewFeedbackUpdated",
  InterviewFeedbackDeleted = "InterviewFeedbackDeleted",
  InterviewCommentAdded = "InterviewCommentAdded",
  InterviewCommentUpdated = "InterviewCommentUpdated",
  InterviewCommentDeleted = "InterviewCommentDeleted",
  OrganizationLinkInvitationAccepted = "OrganizationLinkInvitationAccepted",
}

export const ProcessAutomationActivityTypes = [
  ActivityType.AutoProcessOperationFailed,
  ActivityType.BaseProcessNotFound,
  ActivityType.ProcessModelNotFound,
  ActivityType.OrganizationNotLinked,
  ActivityType.InvalidUserGroupRoleMapping,
] as const;
export type ProcessAutomationActivityType = Union<typeof ProcessAutomationActivityTypes>;

export const DocumentActivityTypes = [
  ActivityType.FileUploaded,
  ActivityType.FileModified,
  ActivityType.FileDeleted,
  ActivityType.FreeFormatFileUploaded,
  ActivityType.FreeFormatFileDeleted,
  ActivityType.FileExpiresSoon,
  ActivityType.DocumentSetMetaDataModified,
] as const;

export type DocumentActivityType = Union<typeof DocumentActivityTypes>;
export const DocumentFileModifiedActivityTypes = [ActivityType.FileModified] as const;
export const FreeFormatFileActivityTypes = [ActivityType.FreeFormatFileUploaded] as const;
export const DocumentFileActivityTypes = [ActivityType.FileUploaded, ActivityType.FileExpiresSoon] as const;

export const PreviewableDocumentFileActivityTypes = [
  ...DocumentFileActivityTypes,
  ...DocumentFileModifiedActivityTypes,
  ...FreeFormatFileActivityTypes,
] as const;

export type PreviewableDocumentFileActivityType = Union<typeof PreviewableDocumentFileActivityTypes>;

export enum AutomationOperationType {
  "CreateProcess" = "CreateProcess",
  "CreateDelegation" = "CreateDelegation",
  "CreateSharing" = "CreateSharing",
  "UpdateSharing" = "UpdateSharing",
}
